'use client'
import { debounce } from 'lodash'
import { useSearchParams, usePathname } from 'next/navigation'
import { memo, useRef, useState, useEffect, useCallback } from 'react'

import Shared from '@/shared'
import Icons from '@/shared/components/icon/Icons'
import Toast from '@/shared/components/Toast'
import { Text } from '@/shared/components/typography'
import {
  useZoomControlYPosition,
  useFilterButtonYPosition,
  useCurrentLocationButtonYPosition,
  useParkinglotLocationButtonYPosition
} from '@/shared/states/mapLayout'

import { SHEET_STATE_TYPE } from '@/enums/detail'

const CurrentLocationButton = ({
  map,
  locationMarker,
  isOn = false,
  setIsOn
}: {
  map: React.MutableRefObject<naver.maps.Map | null>
  locationMarker: string
  isOn?: boolean
  setIsOn?: React.Dispatch<React.SetStateAction<boolean>>
}) => {
  const searchParams = useSearchParams()
  const isNotFullSheet = searchParams.get('sheet') !== SHEET_STATE_TYPE.VISIBLE
  const { positions, setCurrentLocationButtonEl } = Shared.States.useMapLayoutPositions()
  const currentLocationButtonYPosition = useCurrentLocationButtonYPosition()

  const [isErrorOnCurrPos, setIsErrorOnCurrPos] = useState(false)
  const [errorMsgOnCurrPos, setErrorMsgOnCurrPos] = useState('')
  const currMarkerRef = useRef<naver.maps.Marker | null>(null)

  const currentLocationButtonRef = useRef(null)

  useEffect(() => {
    if (currentLocationButtonRef.current == null) return
    setCurrentLocationButtonEl(currentLocationButtonRef.current)
  }, [currentLocationButtonRef.current])

  const moveToCurrentLocation = async (e: React.MouseEvent<HTMLButtonElement>, naverMap: naver.maps.Map | null) => {
    e.preventDefault()
    try {
      const { lat, lng } = await Shared.Utils.getCurrentLatLng()
      const currCenter = new naver.maps.LatLng(lat, lng)

      if (naverMap == null) return

      if (currMarkerRef.current) {
        currMarkerRef.current.setMap(null)
      }

      const currentMarker = new naver.maps.Marker({
        position: currCenter,
        map: naverMap,
        icon: {
          content: locationMarker
        },
        zIndex: 99
      })
      currMarkerRef.current = currentMarker
      naverMap.morph(currCenter)
      if (setIsOn) {
        setIsOn(true)
      }
    } catch (err: unknown) {
      if (err instanceof Shared.Errors.GeolocationError) {
        setErrorMsgOnCurrPos(err.message)
        setIsErrorOnCurrPos(true)
      } else {
        throw err
      }
    }
  }

  return (
    <>
      <div
        ref={currentLocationButtonRef}
        className={`text-dark-gray-010 absolute z-1 flex w-[42px] cursor-pointer flex-col items-center justify-center rounded-full bg-gray-010 shadow-md active:bg-[#DEE4E9]`}
        style={{
          display: isNotFullSheet ? 'flex' : 'none',
          height: `${positions.buttonHeight}px`,
          marginBottom: `${positions.paddingX}px`,
          bottom: `${currentLocationButtonYPosition}px`,
          right: 0
        }}
      >
        <button
          type="button"
          onClick={(e) => moveToCurrentLocation(e, map.current)}
          className="h-full w-full rounded-[100%]"
        >
          <div className="pointer-events-none flex h-full w-full items-center justify-center">
            <Icons.GpsIcon fill="currentColor" color={`${isOn ? '#0099FF' : ''}`} />
          </div>
        </button>
      </div>
      {isErrorOnCurrPos && <Toast text={errorMsgOnCurrPos} onClose={() => setIsErrorOnCurrPos(false)} />}
    </>
  )
}

const ZoomLevelControlButton = ({ mapRef }: { mapRef: React.MutableRefObject<naver.maps.Map | null> }) => {
  const zoomLevelControlRef = useRef<HTMLDivElement | null>(null)
  const mapLayoutPositions = Shared.States.useMapLayoutPositions((state) => state.positions)
  const initialZoomControlYPosition = useZoomControlYPosition()
  const pathname = usePathname()

  const [isShow, setIsShow] = useState(true)
  const [zoomControlYPosition, setZoomControlYPosition] = useState(initialZoomControlYPosition)
  const currentLocationButtonYPosition = useCurrentLocationButtonYPosition()

  const onZoomIn = (e: React.MouseEvent<HTMLButtonElement>, naverMap: naver.maps.Map | null) => {
    e.preventDefault()
    if (naverMap == null) return
    naverMap.setZoom(naverMap.getZoom() + 1, true)
  }

  const onZoomOut = (e: React.MouseEvent<HTMLButtonElement>, naverMap: naver.maps.Map | null) => {
    e.preventDefault()
    if (naverMap == null) return
    naverMap.setZoom(naverMap.getZoom() - 1, true)
  }

  const checkZoomButtonVisibility = useCallback(() => {
    if (!zoomLevelControlRef.current || !mapLayoutPositions.currentLocationButtonEl) return

    const zoomButtonBottom = zoomLevelControlRef.current.getBoundingClientRect().bottom
    const currentButtonTop = mapLayoutPositions.currentLocationButtonEl.getBoundingClientRect().y

    const isVisible = currentLocationButtonYPosition === 16 ? true : zoomButtonBottom < currentButtonTop

    setIsShow(isVisible)
  }, [mapLayoutPositions.currentLocationButtonEl, currentLocationButtonYPosition])

  const updateButtonPosition = useCallback(() => {
    const ticketPattern: RegExp = /\/t\/[0-9]+/
    const myTicketPattern: RegExp = /\/my-ticket\/[0-9]+/

    const windowHeight = window.innerHeight
    const buttonHeight = mapLayoutPositions.buttonHeight

    if (ticketPattern.test(pathname) || myTicketPattern.test(pathname)) {
      return setZoomControlYPosition(windowHeight / 2 - buttonHeight)
    }

    return setZoomControlYPosition(windowHeight / 2)
  }, [pathname, mapLayoutPositions.buttonHeight])

  useEffect(() => {
    updateButtonPosition()
  }, [pathname, updateButtonPosition])

  useEffect(() => {
    const visibilityDebounce = debounce(checkZoomButtonVisibility, 10)
    const resizeDebounce = debounce(updateButtonPosition, 10)

    visibilityDebounce()
    window.addEventListener('resize', resizeDebounce)

    return () => {
      visibilityDebounce.cancel()
      resizeDebounce.cancel()
      window.removeEventListener('resize', resizeDebounce)
    }
  }, [checkZoomButtonVisibility, updateButtonPosition])

  return (
    <div
      ref={zoomLevelControlRef}
      className="text-dark-gray-010 absolute z-1 flex w-[42px] cursor-pointer flex-col items-center justify-center rounded-md bg-gray-010 shadow-120"
      style={{
        height: `${mapLayoutPositions.buttonHeight * 2}px`,
        top: zoomControlYPosition,
        right: 0,
        opacity: isShow ? 1 : 0,
        transition: `opacity ${isShow ? '0.2s' : ''} ease-in-out`
      }}
    >
      <button
        type="button"
        className="zoom_in flex h-[50%] w-[100%] cursor-pointer items-center justify-center rounded-t-[4px] bg-010 active:bg-[#DEE4E9]"
        disabled={!isShow}
        onClick={(e) => onZoomIn(e, mapRef.current)}
      >
        <Icons.ZoomInIcon fill="currentColor" color="#263238" />
      </button>
      <button
        type="button"
        className="zoom_out flex h-[50%] w-[100%] cursor-pointer items-center justify-center rounded-b-[4px] border-t-[1px] border-t-[#DEE4E9] bg-010 active:bg-[#DEE4E9]"
        disabled={!isShow}
        onClick={(e) => onZoomOut(e, mapRef.current)}
      >
        <Icons.ZoomOutIcon fill="currentColor" color="#263238" />
      </button>
    </div>
  )
}

function ParkinglotLocationButton({
  map,
  latitude,
  longitude
}: {
  map: React.MutableRefObject<naver.maps.Map | null>
  latitude: number
  longitude: number
}) {
  const mapLayoutPositions = Shared.States.useMapLayoutPositions((state) => state.positions)
  const parkinglotLocationButtonYPosition = useParkinglotLocationButtonYPosition()

  const moveToParkinglotLocation = async (e: React.MouseEvent<HTMLButtonElement>, naverMap: naver.maps.Map | null) => {
    e.preventDefault()
    if (naverMap == null) return
    const parkinglotCenter = new naver.maps.LatLng(latitude, longitude)
    naverMap.morph(parkinglotCenter)
  }

  return (
    <div
      className="text-dark-gray-010 absolute z-1 flex w-[42px] cursor-pointer flex-col items-center justify-center rounded-full bg-gray-010 shadow-md active:bg-[#DEE4E9]"
      style={{
        height: `${mapLayoutPositions.buttonHeight}px`,
        bottom: `${parkinglotLocationButtonYPosition}px`,
        right: 0
      }}
    >
      <button type="button" onClick={(e) => moveToParkinglotLocation(e, map.current)}>
        <div className="flex h-full w-full items-center justify-center">
          <Icons.LocationIcon stroke="currentColor" color="#263238" />
        </div>
      </button>
    </div>
  )
}

interface FilterButtonProps {
  isFilterOn: boolean
  onFilterButtonClick: () => void
}

const FilterButton = ({ isFilterOn, onFilterButtonClick }: FilterButtonProps) => {
  const filterButtonYPosition = useFilterButtonYPosition()

  return (
    <div
      className={`text-dark-gray-010 absolute z-1 flex h-[42px] w-[42px] cursor-pointer flex-col items-center justify-center rounded-md bg-gray-010 shadow-md active:bg-[#DEE4E9]`}
      style={{ top: `${filterButtonYPosition}px`, right: 0 }}
      onClick={onFilterButtonClick}
    >
      <Text level="tint1" color="text090" weight="regular">
        구매
      </Text>
      <div
        className={`flex h-[14px] w-[30px] items-center justify-center rounded-[4px] ${
          isFilterOn ? 'bg-main-010' : 'bg-gray-020'
        }`}
      >
        <Text level="tint2" color={isFilterOn ? 'main050' : 'text070'} weight={isFilterOn ? 'bold' : 'medium'}>
          {isFilterOn ? 'ON' : 'OFF'}
        </Text>
      </div>
    </div>
  )
}

const memoMapController = {
  CurrentLocationButton: memo(CurrentLocationButton),
  ZoomLevelControlButton: memo(ZoomLevelControlButton),
  ParkinglotLocationButton: memo(ParkinglotLocationButton),
  FilterButton: memo(FilterButton)
}

export default memoMapController
